import type { KnowledgeBaseEntry } from "@/features/knowledge/types/knowledge-base";
import type {
  AiSuggestion,
  SuggestionEvidenceItem,
  SuggestionKind,
  SuggestionMedia,
  SuggestionTargetType,
} from "@/features/ai-suggestions/types/suggestion";

import { KNOWLEDGE_SUGGESTION_CONTACT_OFFSET } from "../config";
import { createId, createPastTimestamp } from "./base";

import {
  SUGGESTION_KIND,
  SUGGESTION_TARGET,
} from "@/features/ai-suggestions/types/suggestion";

interface KnowledgeBaseConfig {
  topic: string;
  description: string;
}

const KNOWLEDGE_BASE_ENTRIES: KnowledgeBaseConfig[] = [
  {
    topic: "Clinic opening hours",
    description:
      "We are open Sunday to Thursday from 10:00 AM to 8:00 PM, and Saturday from 11:00 AM to 6:00 PM. We are closed on Fridays and public holidays.",
  },
  {
    topic: "Booking and rescheduling appointments",
    description:
      "Appointments can be booked through WhatsApp, phone, or our website. To reschedule, please give us at least 24 hours notice to avoid a cancellation fee.",
  },
  {
    topic: "Payment methods accepted",
    description:
      "We accept cash, all major credit and debit cards, and bank transfers. Installment plans are available for treatment packages above a set threshold.",
  },
  {
    topic: "Consultation policy",
    description:
      "First consultations are complimentary for most treatments. During the consultation our specialist assesses your needs and recommends a tailored plan before any procedure begins.",
  },
  {
    topic: "Aftercare and follow-up",
    description:
      "Every procedure comes with detailed aftercare instructions. Follow-up visits are scheduled where clinically needed and are included for most package treatments.",
  },
  {
    topic: "Parking and location",
    description:
      "The clinic offers free underground parking for patients. We are located on the ground floor with step-free access and are a short walk from the nearest metro station.",
  },
  {
    topic: "Treatment comfort and downtime",
    description:
      "Most treatments cause minimal discomfort. Where needed we apply numbing cream or cooling technology. Many treatments have little to no downtime; some may cause mild redness that settles within a day.",
  },
  {
    topic: "Session length and course size",
    description:
      "Most appointments run between 30 and 60 minutes. Results vary by treatment: many patients see results after a single session, while others follow a recommended course of 3 to 6 sessions.",
  },
  {
    topic: "Package refunds and credits",
    description:
      "Unused sessions from a package can be credited toward other treatments. Refunds follow our standard policy, which our team will walk you through on request.",
  },
];

/** One conversation behind a suggested change, before it is tied to a mock
 *  contact and a generated id. */
export type KnowledgeSuggestionEvidenceSeed = Omit<
  SuggestionEvidenceItem,
  "generationId" | "contactId" | "contactName"
>;

interface KnowledgeSuggestionConfig {
  topic: string;
  description: string;
  customerQuestion: string | null;
  distinctContactCount: number;
  kind: SuggestionKind;
  targetType: SuggestionTargetType;
  targetLabel: string | null;
  draftedAnswer?: string;
  suggestedAnswer?: string;
  suggestedAnswerNeedsInput?: boolean;
  draftedMedia?: SuggestionMedia[];
  evidence?: KnowledgeSuggestionEvidenceSeed[];
}

// Generation ids are minted per reload, so the evidence behind them is kept
// here rather than in the store, keyed by the id the suggestion carries.
const evidenceByGenerationId = new Map<
  string,
  KnowledgeSuggestionEvidenceSeed
>();

export const getKnowledgeSuggestionEvidenceSeed = (
  generationId: string,
): KnowledgeSuggestionEvidenceSeed | undefined =>
  evidenceByGenerationId.get(generationId);

// Covers all three destinations so the accept picker's preselect can be
// exercised in dev:mock without hand-editing the store.
// One fixture per card state: every kind, both Apply and Go as the primary
// action, and both the waiting and drafted variants of the headline.
const KNOWLEDGE_SUGGESTIONS: KnowledgeSuggestionConfig[] = [
  {
    topic: "Loyalty or membership program",
    description:
      "Customers repeatedly ask whether the clinic offers a loyalty scheme, membership tier, or points for repeat visits.",
    customerQuestion:
      "Do you have a membership or loyalty program for regulars?",
    distinctContactCount: 7,
    kind: SUGGESTION_KIND.ADD_KB_ENTRY,
    targetType: SUGGESTION_TARGET.BUSINESS_INFO,
    targetLabel: null,
    draftedAnswer:
      "We run a **Glow Club** membership: 10% off every treatment, priority booking, and a free facial after six visits.",
    suggestedAnswer:
      "We are at [full clinic address], and there is parking directly outside the entrance.",
    suggestedAnswerNeedsInput: true,
  },
  {
    topic: "Laser hair removal on tanned skin",
    description:
      "Customers with recent sun exposure ask whether it is safe to proceed. The clinic has not documented the waiting period.",
    customerQuestion: "can i do laser if im tanned?",
    distinctContactCount: 5,
    kind: SUGGESTION_KIND.ADD_FAQ,
    targetType: SUGGESTION_TARGET.TREATMENT,
    targetLabel: "Laser hair removal",
    suggestedAnswer:
      "We ask you to wait about two weeks after any sun exposure or a tan before laser hair removal. Treating tanned skin raises the risk of burns and pigmentation changes, so we would rather reschedule you than take that risk.",
  },
  {
    topic: "Before and after results",
    description:
      "Customers ask to see results before booking. There are no photos on this treatment.",
    customerQuestion: "do you have any before and after pics?",
    distinctContactCount: 4,
    kind: SUGGESTION_KIND.ADD_MEDIA,
    targetType: SUGGESTION_TARGET.TREATMENT,
    targetLabel: "Hydrafacial",
    draftedMedia: [
      {
        url: "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=600",
        type: "image",
        name: "Session 3 result",
      },
    ],
  },
  {
    topic: "Profhilo",
    description:
      "Asked for by name several times. Not in the catalog, so the team may want to add it.",
    customerQuestion: "do you do profhilo?",
    distinctContactCount: 3,
    kind: SUGGESTION_KIND.CREATE_RECORD,
    targetType: SUGGESTION_TARGET.TREATMENT,
    targetLabel: "Profhilo",
  },
  {
    topic: "Years of practice and specialism",
    description:
      "Customers ask how long the doctor has been practising and what they specialise in. Not documented on the profile.",
    customerQuestion: "how experienced is the doctor?",
    distinctContactCount: 2,
    kind: SUGGESTION_KIND.UPDATE_DESCRIPTION,
    targetType: SUGGESTION_TARGET.DOCTOR,
    targetLabel: "Dr. Sarah Ahmed",
  },
  // A change to the agent's own instructions, in the two-line form
  // situational guidance is stored as - the one prompt field with a shape.
  {
    topic: "Use the exact welcome greeting",
    description:
      "Reviewers rewrote the opening message four times last month, each time cutting the list of treatments and timings.",
    customerQuestion: null,
    distinctContactCount: 1,
    kind: SUGGESTION_KIND.IMPROVE_PROMPT,
    targetType: SUGGESTION_TARGET.AGENT_PROMPT,
    targetLabel: "situationalGuidance",
    suggestedAnswer:
      'When: A customer sends a first message such as "hello"\nGuide it to: Greet them with "Good {Morning, Afternoon, Evening}, welcome to Storax Lifestyle Clinic" and ask "How can I help you today?" without listing treatments, timings, location, or consultation options.',
    evidence: [
      {
        customerText: "hello",
        reply:
          "Good morning, welcome to Storax Lifestyle Clinic! We offer laser hair removal, Botox, fillers and skin treatments. We are open 9 to 6 on weekdays. Would you like to book a consultation?",
        outcome: "edited_sent",
        rating: "down",
        reasons: ["too_long"],
        comment: null,
        suggestedReply:
          "Good morning, welcome to Storax Lifestyle Clinic. How can I help you today?",
        steerInstruction: null,
      },
      {
        customerText: "hi there",
        reply:
          "Hi! Welcome to Storax Lifestyle Clinic. Here is what we offer: laser hair removal, Botox, fillers, facials. We are at 12 Park Lane. Book a consultation any time!",
        outcome: "steered",
        rating: null,
        reasons: [],
        comment: null,
        suggestedReply: null,
        steerInstruction: "Just greet and ask how you can help, no list",
      },
    ],
  },
  // The plain single-line form, so both prompt shapes are reachable in dev:mock.
  {
    topic: "Never quote a price without the consultation caveat",
    description:
      "Reviewers flagged three replies that gave a flat price where the final figure depends on the assessment.",
    customerQuestion: null,
    distinctContactCount: 1,
    kind: SUGGESTION_KIND.IMPROVE_PROMPT,
    targetType: SUGGESTION_TARGET.AGENT_PROMPT,
    targetLabel: "businessRules",
    suggestedAnswer:
      "When quoting a price, always say it is a starting price and that the final figure is confirmed at the consultation.",
    evidence: [
      {
        customerText: "how much is laser on the full legs?",
        reply: "Full legs laser is 180.",
        outcome: "discarded",
        rating: null,
        reasons: ["wrong_facts"],
        comment: "Depends on skin and hair type, never a flat number",
        suggestedReply:
          "Full legs laser starts from 180; the final price is confirmed at your consultation once we have assessed your skin and hair.",
        steerInstruction: null,
      },
    ],
  },
];

export function generateKnowledgeBaseEntries(
  organizationId: string,
  workspaceId: string,
): KnowledgeBaseEntry[] {
  return KNOWLEDGE_BASE_ENTRIES.map((config, index) => {
    const timestamp = createPastTimestamp(90 - index);

    return {
      id: createId(),
      topic: config.topic,
      description: config.description,
      organizationId,
      workspaceId,
      createdAt: timestamp,
      updatedAt: timestamp,
    };
  });
}

export function generateKnowledgeSuggestions(
  contactIds: string[] = [],
): AiSuggestion[] {
  return KNOWLEDGE_SUGGESTIONS.map((config, index) => {
    const timestamp = createPastTimestamp(14 - index * 2);

    return {
      id: createId(),
      topic: config.topic,
      description: config.description,
      customerQuestion: config.customerQuestion,
      status: "pending" as const,
      occurrenceCount: config.distinctContactCount,
      distinctContactCount: config.distinctContactCount,
      // Spread gaps across contacts past the tutorial ones so the inbox
      // handover banner has data to show in mock mode.
      sourceContactId:
        contactIds[index + KNOWLEDGE_SUGGESTION_CONTACT_OFFSET] ?? null,
      sourceConversationId: null,
      draftedAnswer: config.draftedAnswer ?? null,
      suggestedAnswer: config.suggestedAnswer ?? null,
      suggestedAnswerNeedsInput: config.suggestedAnswerNeedsInput ?? false,
      draftedMedia: config.draftedMedia ?? [],
      // Named on the drafted rows only, so the headline's "…from Priya's reply"
      // variant is reachable alongside the plain one.
      sourceContactName: config.draftedAnswer ? "Priya Menon" : null,
      kind: config.kind,
      targetType: config.targetType,
      targetLabel: config.targetLabel,
      targetId: null,
      resolvedType: null,
      resolvedId: null,
      source: config.evidence ? "reply_reviews" : "conversation",
      evidence: (config.evidence ?? []).map((seed) => {
        const generationId = createId();

        evidenceByGenerationId.set(generationId, seed);

        return generationId;
      }),
      createdAt: timestamp,
      updatedAt: timestamp,
    };
  });
}
